import { SendDownloadLinksFormView } from '../view/SendDownloadLinksFormView';
import { templateSendDownloadLinksForm } from '../view/template';
import {
  BaseWebComponentController,
  CustomEventListener,
  IListenerConfig, WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { SendDownloadLinksFormWebComponent } from '../component/SendDownloadLinksFormWebComponent';
import { SendDownloadLinksFormRepository } from '../repository/SendDownloadLinksFormRepository';
import { OnSendDownloadLinksPanelClose, OnSendDownloadLinksPanelSend } from '../events/SendDownloadLinksEvents';
import { Subscription } from 'rxjs';
import { PackagesToastNotification } from '../../uiElements/notifications/toastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { DownloadKitInterface, SendDownloadLinksResponse } from '../../types/types';
import { MonthlyProductType } from '../../../license/types/MonthlyProductType';
import { translateFn } from '../../../utils/translator.helper';
import { RINGS, ringToDownloadUrlNameMap } from '../types/constants';
import { Feature } from '@nirvana/shared/constants';

export class SendDownloadLinksFormController extends BaseWebComponentController {
  public component: SendDownloadLinksFormWebComponent;
  public view: SendDownloadLinksFormView;
  public viewConstructor: typeof SendDownloadLinksFormView;
  public dataRepository: SendDownloadLinksFormRepository;
  public dataRepositoryConstructor: typeof SendDownloadLinksFormRepository;
  private validSubscription: Subscription | null;
  private ringToFormMapping: any;
  private downloadUrls: any;

  constructor(component: SendDownloadLinksFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: SendDownloadLinksFormView,
      viewTemplate: templateSendDownloadLinksForm,
      dataRepositoryConstructor: SendDownloadLinksFormRepository,
    });

  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.cancelSendDownloadForm,
      },
      {
        element: this.view.dom.$sendDownloadLinksButton,
        event: 'click',
        callback: this.sendLinksSendDownloadForm,
      },
      {
        element: this.view.dom.$emailAddressFormField,
        event: 'change',
        callback: this.updateSendButtonStatus,
      },
      {
        element: this.view.dom.$ringSelectorDropDown,
        event: 'selectionChange',
        callback: this.updateFormValues,
      }
    ];
  }

  private getDisplayLinkValueForRing(basePath, selectedOption, platform): string {

    if(this.downloadUrls[ringToDownloadUrlNameMap[selectedOption.selectedValue][platform]] === "") {
        return basePath + this.downloadUrls[ringToDownloadUrlNameMap[0][platform]]
    }
    return basePath + this.downloadUrls[ringToDownloadUrlNameMap[selectedOption.selectedValue][platform]]
  }

  private updateFormValues(e: CustomEvent) {

      if (this.dependencies.isCloud() && this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE)) {
        this.view.dom.$ringSelectorFormField.classList.remove("hidden");
      }

      const selectedOption = e.detail;
      const basePath = this.getBasePath();

      if (!selectedOption.selectedValue || selectedOption.selectedValue === "" || selectedOption.selectedValue === null || selectedOption.selectedValue === undefined) {
        selectedOption.selectedValue = RINGS.SLOW_RING;
      }

      const urlWin = this.getDisplayLinkValueForRing(basePath, selectedOption, 'win');
      const urlNix = this.getDisplayLinkValueForRing(basePath, selectedOption, 'nix');
      const urlMac = this.getDisplayLinkValueForRing(basePath, selectedOption, 'mac');

      this.view.dom.$windowsDownloaderLinkDisplay.innerText = this.manageKitLinkLengthDisplayed(urlWin);
      this.view.dom.$linuxInstallerLinkDisplay.innerText = this.manageKitLinkLengthDisplayed(urlNix);
      this.view.dom.$macosDownloaderLinkDisplay.innerText = this.manageKitLinkLengthDisplayed(urlMac);

      this.view.dom.$windowsDownloaderLink.value = urlWin;
      this.view.dom.$linuxInstallerLink.value = urlNix;
      this.view.dom.$macosDownloaderLink.value = urlMac;

  }

  private updateSendButtonStatus(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = this.view.dom.$emailAddressFormField.valid$.subscribe({
      next: valid => {
        this.view.dom.$sendDownloadLinksButton.disabled = !valid;
      },
    });
  }

  private cancelSendDownloadForm(): void {
    this.component.dispatchEvent(new OnSendDownloadLinksPanelClose());
  }

  private async sendLinksSendDownloadForm(): Promise<void> {
    try {

      await this.sendDownloadLinksEmail(
        this.component.getPackageId(),
        this.view.dom.$emailAddressFormField.value as Array<string>,
        this.view.dom.$ringSelectorDropDown.value
      );
    } catch (error: any) {
      this.createErrorToastNotification(error.err.exceptionType);
    }
    this.component.dispatchEvent(new OnSendDownloadLinksPanelSend());
  }

  private async getRingSelectorOptions(): Promise<any[]> {

    let productionRingDisabled = true;
    let test1RingDisabled = true;
    let test2RingDisabled = true;

    if (this.downloadUrls['kit-bstwin-production'] || this.downloadUrls['kit-bstnix-production'] || this.downloadUrls['kit-mac-downloader-production']) {
      productionRingDisabled = false;
    }

    if (this.downloadUrls['kit-bstwin-test1'] || this.downloadUrls['kit-bstnix-test1'] || this.downloadUrls['kit-mac-downloader-test1']) {
      test1RingDisabled = false;
    }

    if (this.downloadUrls['kit-bstwin-test2'] || this.downloadUrls['kit-bstnix-test2'] || this.downloadUrls['kit-mac-downloader-test2']) {
      test2RingDisabled = false;
    }

    return [
      {id: RINGS.SLOW_RING, label: translateFn("Slow ring"), value: RINGS.SLOW_RING, disabled: false},
      {id: RINGS.PRODUCTION_RING, label: translateFn("Production ring"), value: RINGS.PRODUCTION_RING, disabled: productionRingDisabled},
      {id: RINGS.TEST1_RING, label: translateFn("Test ring 1"), value: RINGS.TEST1_RING, disabled: test1RingDisabled},
      {id: RINGS.TEST2_RING, label: translateFn("Test ring 2"), value: RINGS.TEST2_RING, disabled: test2RingDisabled}
    ];

  }

  public async getDataForView() {

    this.downloadUrls = await this.component.getPackageLinks();
    this.view.dom.$ringSelectorDropDown.cannotDelete = true;
    this.view.dom.$ringSelectorDropDown.filterOptions = await this.getRingSelectorOptions();
    this.view.dom.$ringSelectorDropDown.value = RINGS.SLOW_RING;

    const basePath = this.getBasePath();
    const kits = new Array<DownloadKitInterface>();

    const winKitPath = basePath + this.downloadUrls['kit-bstwin'];
    const nixKitPath = basePath + this.downloadUrls['kit-bstnix'];
    const macDownloaderKitPath = basePath + this.downloadUrls['kit-mac-downloader'];

    this.view.dom.$windowsDownloaderLinkDisplay.innerText = this.manageKitLinkLengthDisplayed(winKitPath);
    this.view.dom.$linuxInstallerLinkDisplay.innerText = this.manageKitLinkLengthDisplayed(nixKitPath);
    this.view.dom.$macosDownloaderLinkDisplay.innerText = this.manageKitLinkLengthDisplayed(macDownloaderKitPath);

    this.view.dom.$windowsDownloaderLink.value = winKitPath;
    this.view.dom.$linuxInstallerLink.value = nixKitPath;
    this.view.dom.$macosDownloaderLink.value = macDownloaderKitPath;


    kits.push({
      field: this.view.dom.$windowsDownloaderFormField,
      enabled: true,
    });

    if (
      this.component.getPackageProductType() === undefined ||
      this.component.getPackageProductType() === MonthlyProductType.EndpointSecurity
    ) {
      kits.push({
        field: this.view.dom.$linuxInstallerFormField,
        enabled: true,
      });

      kits.push({
        field: this.view.dom.$macosDownloaderFormField,
        enabled: true,
      });

    } else {
      kits.push({
        field: this.view.dom.$linuxInstallerFormField,
        enabled: false,
      });

      kits.push({
        field: this.view.dom.$macosDownloaderFormField,
        enabled: false,
      });
    }

    return kits;
  }

  private createErrorToastNotification(exception: string): void {
    let errorMessage: string;
    switch (exception) {
      case 'DAL\\Exceptions\\SecurityException':
        errorMessage = translateFn(
          'The selected installation package is not available. Please refresh the installation packages list.'
        );
        break;
      case 'Exceptions\\CannotSendEmailException':
        errorMessage = translateFn(
          'Cannot send the email. Go to the Configuration page, Mail Server tab, and configure the mail server settings.'
        );
        break;
      default:
        errorMessage = translateFn('An unknown error has occurred.');
        break;
    }
    PackagesToastNotification.createToastNotification({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  private getBasePath(): string {
    return 'https://' + window.location.hostname;
  }

  private manageKitLinkLengthDisplayed(kitLink: string): string {
    const linkHrefTextLengthDisplayed = 90;
    if (kitLink.length > linkHrefTextLengthDisplayed) {
      kitLink = kitLink.substring(0, linkHrefTextLengthDisplayed) + '..';
    }
    return kitLink;
  }

  private sendDownloadLinksEmail(packageId: string, emails: Array<string>, ringId: string | number): Promise<SendDownloadLinksResponse> {
    return this.dataRepository.sendDownloadLinksEmail(packageId, emails, ringId);
  }
}
